import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { Calendar, ArrowRight, Search, Tag } from 'lucide-react';


const CATEGORIES = [
    'AI & Automation',
    'Sales Strategy',
    'Remote Work',
    'Leadership',
    'Technology',
    'Outsourcing'
];

const Blog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                // Fetch only published posts
                const q = query(
                    collection(db, 'posts'),
                    where('published', '==', true)
                );
                const querySnapshot = await getDocs(q);
                const postsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Sort client-side to avoid index issues
                postsData.sort((a, b) => {
                    const dateA = a.createdAt?.seconds || 0;
                    const dateB = b.createdAt?.seconds || 0;
                    return dateB - dateA;
                });

                setPosts(postsData);
            } catch (error) {
                console.error("Error fetching posts: ", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    return (
        <div className="pt-20 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
            <SEO
                title="BPO Insights & AI Trends | The Blog"
                description="Read the latest articles on AI in outsourcing, remote team management, and cost optimization strategies."
            />
            <div className="max-w-7xl mx-auto py-12">
                <FadeIn>
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-[#20222D] mb-4">Blog & Insights</h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Expert perspectives on AI, outsourcing, and the future of work.
                        </p>
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A2CB52] mx-auto"></div>
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
                            <p className="text-gray-500 text-lg">No posts published yet. Check back soon!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
                            {/* Main Content Area */}
                            <div className="lg:col-span-3 space-y-12">

                                {/* Featured Post */}
                                {posts.length > 0 && (
                                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden group">
                                        <div className="grid md:grid-cols-2 gap-0">
                                            <div className="relative h-64 md:h-auto overflow-hidden">
                                                {posts[0].imageUrl ? (
                                                    <img
                                                        src={posts[0].imageUrl}
                                                        alt={posts[0].title}
                                                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">No Image</div>
                                                )}
                                                <div className="absolute top-4 left-4 bg-[#A2CB52] text-[#20222D] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                                                    Featured
                                                </div>
                                            </div>
                                            <div className="p-8 flex flex-col justify-center">
                                                <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
                                                    <span className="flex items-center text-[#A2CB52] font-semibold">
                                                        <Tag size={14} className="mr-1" />
                                                        {posts[0].category || 'Insight'}
                                                    </span>
                                                    <span className="flex items-center">
                                                        <Calendar size={14} className="mr-1" />
                                                        {posts[0].createdAt?.seconds ? new Date(posts[0].createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                                    </span>
                                                </div>
                                                <h2 className="text-3xl font-bold text-[#20222D] mb-4 leading-tight">
                                                    <Link to={`/blog/${posts[0].slug || posts[0].id}`} className="hover:text-[#A2CB52] transition-colors">
                                                        {posts[0].title}
                                                    </Link>
                                                </h2>
                                                <p className="text-gray-600 mb-6 line-clamp-3">
                                                    {posts[0].excerpt}
                                                </p>
                                                <Link
                                                    to={`/blog/${posts[0].slug || posts[0].id}`}
                                                    className="inline-flex items-center text-[#20222D] font-bold hover:text-[#A2CB52] transition-colors"
                                                >
                                                    Read Full Story <ArrowRight size={18} className="ml-2" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Recent Posts Grid */}
                                <div className="grid md:grid-cols-2 gap-8">
                                    {posts.slice(1).map((post) => (
                                        <article key={post.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow flex flex-col h-full group">
                                            <div className="relative h-48 overflow-hidden">
                                                {post.imageUrl ? (
                                                    <img
                                                        src={post.imageUrl}
                                                        alt={post.title}
                                                        className="h-full w-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <div className="h-full w-full bg-gray-200 flex items-center justify-center text-gray-400">
                                                        No Image
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-6 flex-1 flex flex-col">
                                                <div className="flex items-center text-xs text-gray-500 mb-3 space-x-3">
                                                    <span className="text-[#A2CB52] font-semibold uppercase tracking-wide">
                                                        {post.category || 'Article'}
                                                    </span>
                                                    <span>•</span>
                                                    <span className="flex items-center">
                                                        {post.createdAt?.seconds ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                                    </span>
                                                </div>
                                                <h2 className="text-xl font-bold text-[#20222D] mb-3 line-clamp-2">
                                                    <Link to={`/blog/${post.slug || post.id}`} className="hover:text-[#A2CB52] transition-colors">
                                                        {post.title}
                                                    </Link>
                                                </h2>
                                                <p className="text-gray-600 mb-4 line-clamp-3 text-sm flex-1">
                                                    {post.excerpt}
                                                </p>
                                                <Link
                                                    to={`/blog/${post.slug || post.id}`}
                                                    className="inline-flex items-center text-[#A2CB52] font-bold hover:underline mt-auto text-sm"
                                                >
                                                    Read More <ArrowRight size={14} className="ml-1" />
                                                </Link>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </div>

                            {/* Sidebar */}
                            <div className="lg:col-span-1 space-y-8">
                                {/* Search Widget */}
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-[#20222D] mb-4">Search</h3>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search articles..."
                                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none"
                                        />
                                        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    </div>
                                </div>

                                {/* Categories Widget */}
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-[#20222D] mb-4">Categories</h3>
                                    <div className="space-y-2">
                                        {CATEGORIES.map(cat => (
                                            <Link
                                                key={cat}
                                                to={`/blog/category/${encodeURIComponent(cat)}`}
                                                className="block p-3 rounded-lg hover:bg-gray-50 text-gray-600 hover:text-[#A2CB52] transition-colors flex justify-between items-center group"
                                            >
                                                <span>{cat}</span>
                                                <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </Link>
                                        ))}
                                    </div>
                                </div>

                                {/* Newsletter Widget */}
                                <div className="bg-[#20222D] p-6 rounded-2xl shadow-lg text-white">
                                    <h3 className="text-lg font-bold mb-2">Subscribe</h3>
                                    <p className="text-gray-400 text-sm mb-4">Get the latest insights delivered to your inbox.</p>
                                    <input
                                        type="email"
                                        placeholder="Your email address"
                                        className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-[#A2CB52] focus:ring-1 focus:ring-[#A2CB52] outline-none mb-3"
                                    />
                                    <button className="w-full bg-[#A2CB52] text-[#20222D] font-bold py-3 rounded-lg hover:bg-[#B5D575] transition-colors">
                                        Subscribe
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </FadeIn>
            </div>
        </div>
    );
};

export default Blog;
